import { useMemo } from 'react';
import LazyLoad from 'react-lazyload';
import Button from '@alltrails/shared/denali/components/Button';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { DESKTOP_BREAKPOINT } from 'utils/constants/breakpoints';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import useUser from 'hooks/useUser';
import logHomepageProUpsellBlockButtonClicked from '@alltrails/analytics/events/logHomepageProUpsellBlockButtonClicked';
import FeatureRow from './FeatureRow';
import * as styles from './styles/styles.module.scss';
import logoPro from './logo_pro.png';
import proHeader from './pro_header.png';

const BG_IMAGE_BASE = 'https://cdn-assets.alltrails.com/assets/images/homepage/pro_upsell/';

type Props = {
  windowWidth: number;
};

export default function ProUpsell({ windowWidth }: Props) {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();

  const featuresContainerStyle = useMemo(() => {
    const isDesktop = windowWidth && windowWidth >= DESKTOP_BREAKPOINT;

    if (!isDesktop) {
      return {};
    }

    const fileName = `bg_desktop_${languageRegionCode}.jpg`;
    const backgroundImageUrl = `${BG_IMAGE_BASE}${fileName}`;

    return {
      backgroundImage: `url(${backgroundImageUrl})`
    };
  }, [windowWidth]);

  const upperContainerStyle = {
    backgroundImage: `url(${BG_IMAGE_BASE}bg_mobile_${languageRegionCode}.jpg)`
  };

  return (
    <div className={styles.container}>
      <div style={upperContainerStyle} className={styles.upperContainer} />
      <div style={featuresContainerStyle} className={styles.featuresContainer}>
        <LazyLoad>
          <img className={styles.logoProImage} src={logoPro} alt="" />
        </LazyLoad>
        <h2 className={styles.title}>
          <FormattedMessage defaultMessage="Stay on track while offline with Pro" />
        </h2>
        <div className={styles.headerRow}>
          <div className={[styles.tableHeader, styles.headerRowFeature].join(' ')}>
            <FormattedMessage defaultMessage="features" />
          </div>
          <div className={[styles.tableHeader, styles.headerRowFreePro].join(' ')}>
            <FormattedMessage defaultMessage="free" />
          </div>
          <div className={styles.headerRowFreePro}>
            <LazyLoad>
              <img src={proHeader} className={styles.headerRowProIcon} alt="" />
            </LazyLoad>
          </div>
        </div>
        <FeatureRow free tint text={<FormattedMessage defaultMessage="Favorite trails and create lists" />} />
        <FeatureRow free tint={false} text={<FormattedMessage defaultMessage="Log your activities" />} />
        <FeatureRow free={false} tint text={<FormattedMessage defaultMessage="Download offline maps" />} />
        <FeatureRow free={false} tint={false} text={<FormattedMessage defaultMessage="See real-time overlays" />} />
        <FeatureRow free={false} tint text={<FormattedMessage defaultMessage="Print maps for backup" />} />
        <FeatureRow free={false} tint={false} text={<FormattedMessage defaultMessage="Get off-route notifications" />} />
        <div className={styles.buyLink}>
          <Button
            text={
              !user || user.isPromoEligible ? <FormattedMessage defaultMessage="Try Pro for free" /> : <FormattedMessage defaultMessage="Buy Pro" />
            }
            linkInfo={{ href: LanguageSupportUtil.wrapUrlSafe('/pro?ref=homepage_pro_proupsell', languageRegionCode) }}
            onClick={logHomepageProUpsellBlockButtonClicked}
            testId="pro-upsell-try-or-buy-pro"
            variant="primary"
          />
        </div>
      </div>
    </div>
  );
}
